import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IFormDialogProps } from '~/components/molecules/FormDialog';
import type { IImageCreatorFormProps } from './ImageCreatorForm';
import { FormDialog } from '~/components/molecules/FormDialog';
import { ImageCreatorForm } from './ImageCreatorForm';

export type IImageCreatorFormDialogProps = Pick<
  IFormDialogProps,
  'opened' | 'onClose'
> &
  IImageCreatorFormProps & {
    onSuccess: (id: string, file: File) => void;
  };

export const ImageCreatorFormDialog: React.FC<IImageCreatorFormDialogProps> = (
  props,
) => {
  const { opened, onClose, onSuccess, ...other } = props;
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState<number | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleClose = (): void => {
    setIsSubmitting(false);
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    onClose?.();
  };

  return (
    <FormDialog
      opened={opened}
      onClose={handleClose}
      headline={t('ImageCreatorFormDialog.headline')}
      submitButtonLabel={t('common.insert')}
      size="lg"
      submitting={isSubmitting}
      submitButtonLoadingText={progress ? `${Math.round(progress * 100)}%` : ''}
    >
      {({ formId }) => (
        <ImageCreatorForm
          id={formId}
          extended
          hideActionBar
          onSubmit={() => {
            setProgress(0);
            setIsSubmitting(true);
            abortControllerRef.current = new AbortController();
          }}
          onProgressChange={(event) => setProgress(event.progress)}
          onSuccess={(...args) => {
            setIsSubmitting(false);
            handleClose();
            onSuccess(...args);
          }}
          onFailure={() => setIsSubmitting(false)}
          abortController={abortControllerRef.current ?? undefined}
          {...other}
        />
      )}
    </FormDialog>
  );
};
